import React from 'react';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionIcon from '@mui/icons-material/Description';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import CustomIconBtn from 'src/components/UI/button/CustomIconBtn';
import FhqPreviewModal from './FhqPreviewModal';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { copyToClipBoard } from 'src/util/util';
import axios from 'src/axios';
import {
  APICONSTANTS,
  CONSTANTS,
  GLOBAL_CONSTANTS
} from 'src/components/Constants';
import {
  AppContext,
  LoadingContext,
  SnackContext,
  ThemeContext
} from 'src/store/ContextStore';
import { EVENTS } from '../admin/appReducer';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';
import { originWithBase } from 'src/util/basePath';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export const createUniqueLink = (token) => {
  return `${originWithBase()}/questionnaire?${
    GLOBAL_CONSTANTS.tokenKey
  }=${token}`;
};

export default function TableActions({
  _id,
  email,
  submitted,
  imported,
  token,
  setRevertDialog,
  setEditSpecialtiesDialog,
  linkParameters,
  linkStatus,
  rowIndex,
  merged,
  hasMergedFamily,
  familyId,
  isReferralFirst,
  realProbandId,
  realFamilyId
}) {
  const t = useAutoTranslation();

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [fhqPreviewOpen, setFhqPreviewOpen] = React.useState(false);
  const { setSnack } = React.useContext(SnackContext);
  const { dispatchUpdate } = React.useContext(AppContext);
  const { setLoading: setGlobalLoading } = React.useContext(LoadingContext);
  const { theme } = React.useContext(ThemeContext);
  const { readOnly: roFn } = React.useContext(SgAccessContext);
  const readOnly = roFn('fhqs');

  const openPedigree = (targetProbandId, targetFamilyId) => (e) => {
    e.preventDefault();
    if (!targetProbandId || !targetFamilyId) return;
    setGlobalLoading(true);
    axios
      .get(APICONSTANTS.getFamiliesInfo.replace(':user_id', targetProbandId))
      .then((res) => {
        setGlobalLoading(false);
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: {
            family: res.data,
            selectedUser: {
              _id: targetProbandId,
              familyId: targetFamilyId,
              email
            }
          }
        });
        navigate(CONSTANTS.pedigreeChartPath.replace(':_id', targetFamilyId));
      })
      .catch(() => {
        setGlobalLoading(false);
        setSnack({
          status: true,
          msg: t('Unable to open pedigree'),
          severity: 'error'
        });
      });
  };

  const readOnlyMessage = (msg = 'Read-only: action is not permitted.') => {
    setSnack({ status: true, msg, severity: 'warning' });
  };
  const clickCopyHandler = () => {
    copyToClipBoard(createUniqueLink(token));
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  const sendEmailHandler = (e) => {
    e.preventDefault();
    if (readOnly) return readOnlyMessage(t('Read-only: cannot send emails.'));
    setLoading(true);
    axios
      .post(APICONSTANTS.sendLinkEmail, {
        userEmail: email,
        userLink: createUniqueLink(token)
      })
      .then(() => {
        setLoading(false);
        setSnack({
          status: true,
          msg: t('E-mail sent successfully'),
          severity: 'success'
        });
      })
      .catch(() => {
        setLoading(false);
        setSnack({
          status: true,
          msg: t('Error sending email, please make sure SMTP details are set'),
          severity: 'error'
        });
      });
  };
  const deactivateLink = (e) => {
    e.preventDefault();
    if (readOnly)
      return readOnlyMessage(t('Read-only: cannot activate/deactivate links.'));
    axios
      .post(APICONSTANTS.deactivateLink, { id: _id })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Link deactivated!'),
          severity: 'success'
        });
        dispatchUpdate({ type: EVENTS.DEACTIVATE_LINK, value: rowIndex });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Error deactivating link'),
          severity: 'error'
        });
      });
  };

  return (
    <div style={{ minWidth: 135 }}>
      <CustomIconBtn
        tooltip={copied ? t('Copied to clipboard!') : t('Copy Link')}
        icon={<FileCopyIcon fontSize="small" />}
        onClick={clickCopyHandler}
        disabled={imported}
      />
      {linkStatus && (
        <CustomIconBtn
          tooltip={t('Send E-mail')}
          icon={
            loading ? (
              <CircularProgress size={15} />
            ) : (
              <EmailIcon fontSize="small" />
            )
          }
          disabled={readOnly || loading || imported}
          onClick={sendEmailHandler}
        />
      )}
      {linkStatus && !submitted && setEditSpecialtiesDialog && (
        <CustomIconBtn
          tooltip={t('Edit specialties')}
          icon={<EditOutlinedIcon fontSize="small" />}
          disabled={readOnly || imported}
          onClick={(e) => {
            e.preventDefault();
            if (readOnly)
              return readOnlyMessage(t('Read-only: cannot edit links.'));
            setEditSpecialtiesDialog({
              open: true,
              id: _id,
              email,
              linkParameters: Array.isArray(linkParameters)
                ? linkParameters
                : []
            });
          }}
        />
      )}
      {submitted && !merged && !hasMergedFamily && (
        <CustomIconBtn
          tooltip={t('Revert to Draft')}
          icon={<DescriptionIcon fontSize="small" />}
          onClick={() => {
            if (readOnly)
              return readOnlyMessage(t('Read-only: cannot revert to draft.'));
            setRevertDialog({
              open: true,
              id: _id,
              email,
              isActivating: false
            });
          }}
          disabled={readOnly || imported}
        />
      )}
      {submitted &&
        !merged &&
        (isReferralFirst ? (
          <>
            {realFamilyId && (
              <CustomIconBtn
                type="view-pedigree"
                tooltip={t('View Pedigree')}
                onClick={openPedigree(realProbandId, realFamilyId)}
                color={theme?.buttonStyle}
              />
            )}
            {familyId && (
              <CustomIconBtn
                type="view"
                tooltip={t('Preview FHQ')}
                onClick={(e) => {
                  e.preventDefault();
                  setFhqPreviewOpen(true);
                }}
                color={theme?.buttonStyle}
              />
            )}
          </>
        ) : (
          familyId && (
            <CustomIconBtn
              type="view-pedigree"
              tooltip={t('View Pedigree')}
              onClick={openPedigree(_id, familyId)}
              color={theme?.buttonStyle}
            />
          )
        ))}
      {!submitted &&
        (linkStatus ? (
          <Tooltip title={t('Deactivate link')}>
            <span>
              <IconButton
                size="small"
                onClick={deactivateLink}
                disabled={readOnly}
                sx={{
                  color: 'action.active',
                  '&:hover': {
                    color: '#e41a52',
                    backgroundColor: 'rgba(228,26,82,0.08)'
                  }
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={t('Activate link')}>
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (readOnly)
                    return readOnlyMessage(
                      t('Read-only: cannot activate links.')
                    );
                  setRevertDialog({
                    open: true,
                    id: _id,
                    email,
                    isActivating: true
                  });
                }}
                disabled={readOnly}
                sx={{
                  color: 'action.active',
                  '&:hover': {
                    color: '#43b480',
                    backgroundColor: 'rgba(67,180,128,0.08)'
                  }
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ))}
      {isReferralFirst && (
        <FhqPreviewModal
          open={fhqPreviewOpen}
          onClose={() => setFhqPreviewOpen(false)}
          shadowFamilyId={familyId}
          realFamilyId={realFamilyId}
        />
      )}
    </div>
  );
}
